import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Input, Button, UncontrolledTooltip } from 'reactstrap';
import { faArrowAltCircleRight, faEye, faEyeSlash, faMinusSquare } from '@fortawesome/free-solid-svg-icons';
import style from './style';
import { isFieldConfigurationConflict } from 'Services/fieldConfiguration';

const DataTypeItem = ({
  dataType = {},
  departmentId,
  windowWidth,
  updateDataType,
  isUpdatingDataType = false,
  eanbledChoices,
  selectOptions = [],
  numberOfEnabled,
}) => {
  const [type, setType] = useState(1);

  const { Alias, SortOrder, Type } = dataType;
  let aliasTimeOut = null;

  const sortOptions = selectOptions.slice();

  useEffect(() => {
    setType(dataType.Type || 1);
  }, []);

  const updateValue = (key, value) => {
    if (isFieldConfigurationConflict(dataType)) return;
    const newObject = {};
    newObject[`${dataType.Name}`] = {};
    newObject[`${dataType.Name}`][key] = value;
    if (dataType && dataType.pk_DepartmentDataType) {
      newObject[`${dataType.Name}`].pk_DepartmentDataType = dataType.pk_DepartmentDataType;
    }

    if (key === 'Type') {
      newObject[`${dataType.Name}`].UIType = value;
    }
    if (key === 'Enabled') {
      if (value) {
        newObject[`${dataType.Name}`].SortOrder = numberOfEnabled + 1;
        // newObject[`${dataType.Name}`].listOrigin = 'disabled';
      } else {
        newObject[`${dataType.Name}`].listOrigin = 'enabled';
      }
    }

    updateDataType(newObject, key === 'SortOrder' || (key === 'Enabled' && !value) ? true : false);
  };

  const renderActiveItemFields = () => {
    let isEditable = false;
    if (dataType.Name && dataType.Name.indexOf('CustomField') == 0) {
      isEditable = true;
    }

    if (windowWidth < 980) {
      return (
        <div
          style={{
            ...style.manageFieldsRow,
            display: 'flex',
            flexDirection: 'row',
            gap: 15,
            padding: 15,
            border: '1px solid #ddd',
            borderRadius: 8,
            backgroundColor: 'var(--rr-surface-subtle)',
            margin: '10px 0',
          }}
          key={`${dataType.Name}_${dataType.SortOrder}`}
          id={`${dataType.Name}_${dataType.SortOrder}`}
        >
          {/* Left column - Sort Order and Remove Button */}
          <div
            style={{
              width: 100,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              height: '100%',
              gap: 10,
            }}
          >
            <div style={{ width: '100%' }}>
              <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 5, textAlign: 'left' }}>Sort Order</div>
              <Input
                bsSize="sm"
                disabled={isUpdatingDataType}
                type="select"
                name="select"
                id="exampleSelect"
                defaultValue={SortOrder}
                value={SortOrder}
                style={{ width: '100%' }}
                onChange={(e) => {
                  updateValue('SortOrder', parseInt(e.target.value));
                }}
              >
                {sortOptions}
              </Input>
            </div>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="sm"
                color="danger"
                disabled={isUpdatingDataType}
                onClick={() => {
                  if (updateDataType) {
                    updateValue('Enabled', false);
                  }
                }}
              >
                <FontAwesomeIcon icon={faMinusSquare} />
              </Button>
            </div>
          </div>

          {/* Right column - Type, Alias, and Visibility buttons */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 15,
              alignItems: 'flex-start',
            }}
          >
            {/* Type Select */}
            <div style={{ minWidth: 150, flex: '1 1 150px' }}>
              <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 5 }}>Type</div>
              <Input
                bsSize="sm"
                disabled={isUpdatingDataType}
                type="select"
                name="select"
                value={type || 3}
                style={{ width: '100%' }}
                onChange={(e) => {
                  updateValue('Type', parseInt(e.target.value));
                  setType(e.target.value);
                }}
              >
                <option value={1}>Score</option>
                <option value={2}>Text</option>
                <option value={3}>Yes/No</option>
              </Input>
            </div>

            {/* Alias Input */}
            <div style={{ minWidth: 200, flex: '2 1 200px' }}>
              <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 5 }}>Alias</div>
              <Input
                bsSize="sm"
                autocomplete={`field_${dataType.Name}`}
                id={`${dataType.Name}_${dataType.SortOrder}_Alias`}
                disabled={!isEditable}
                defaultValue={`${Alias || ''}`}
                style={{ width: '100%' }}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (aliasTimeOut) {
                    clearTimeout(aliasTimeOut);
                  }

                  aliasTimeOut = setTimeout(() => {
                    updateValue('Alias', newValue);
                  }, 1000);
                }}
              />
              <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 'bold', marginTop: 2 }}>
                {isEditable ? dataType.Name : null}
              </div>
            </div>

            {/* Rank Visibility */}
            <div style={{ minWidth: 150, flex: '1 1 150px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button
                size="sm"
                id={`DisplayOnRank_${dataType.SortOrder}_${dataType.Name}`}
                color={dataType.DisplayOnRank ? 'success' : 'danger'}
                disabled={isUpdatingDataType}
                onClick={(e) => {
                  updateValue('DisplayOnRank', !dataType.DisplayOnRank);
                }}
              >
                <FontAwesomeIcon icon={dataType.DisplayOnRank ? faEye : faEyeSlash} />
              </Button>
              <div style={{ fontSize: 12 }}>{dataType.DisplayOnRank ? 'Visible' : 'Not Visible'} in Rank</div>
            </div>

            {/* Evaluator Visibility */}
            <div style={{ minWidth: 150, flex: '1 1 150px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button
                size="sm"
                color={dataType.DisplayToEvaluator ? 'success' : 'danger'}
                disabled={isUpdatingDataType}
                onClick={(e) => {
                  updateValue('DisplayToEvaluator', !dataType.DisplayToEvaluator);
                }}
              >
                <FontAwesomeIcon
                  id={`DisplayToEvaluator_${dataType.SortOrder}`}
                  icon={dataType.DisplayToEvaluator ? faEye : faEyeSlash}
                />
              </Button>
              <div style={{ fontSize: 12 }}>
                {dataType.DisplayToEvaluator ? 'Visible' : 'Not Visible'} for Evaluators
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div
          style={style.manageFieldsRow}
          key={`${dataType.Name}_${dataType.SortOrder}`}
          id={`${dataType.Name}_${dataType.SortOrder}`}
        >
          <div>
            <Button
              color="danger"
              disabled={isUpdatingDataType}
              onClick={() => {
                if (updateDataType) {
                  updateValue('Enabled', false);
                }
              }}
            >
              <FontAwesomeIcon icon={faMinusSquare} />
            </Button>
          </div>
          <div style={style.manageFieldsSortOrder}>
            <Input
              disabled={isUpdatingDataType}
              type="select"
              name="select"
              id="exampleSelect"
              defaultValue={SortOrder}
              value={SortOrder}
              onChange={(e) => {
                updateValue('SortOrder', parseInt(e.target.value));
              }}
            >
              {sortOptions}
            </Input>
          </div>
          <div style={{ ...style.manageFieldsSortOrder, width: '24%' }}>
            <Input
              disabled={isUpdatingDataType}
              type="select"
              name="select"
              value={type || 3}
              onChange={(e) => {
                updateValue('Type', parseInt(e.target.value));
                setType(e.target.value);
              }}
            >
              <option value={1}>Score</option>
              <option value={2}>Text </option>
              <option value={3}>Yes/No</option>
            </Input>
          </div>
          <div style={{ ...style.manageFieldsName, width: '54%' }}>
            <Input
              autocomplete={`field_${dataType.Name}`}
              id={`${dataType.Name}_${dataType.SortOrder}_Alias`}
              disabled={!isEditable}
              defaultValue={`${Alias || ''}`}
              onChange={(e) => {
                const newValue = e.target.value;
                if (aliasTimeOut) {
                  clearTimeout(aliasTimeOut);
                }

                aliasTimeOut = setTimeout(() => {
                  updateValue('Alias', newValue);
                }, 1000);
              }}
            />
            <div style={{ textAlign: 'center', fontSize: 12, fontWeight: 'bold' }}>
              {isEditable ? dataType.Name : null}
            </div>
          </div>

          <div>
            <Button
              id={`DisplayOnRank_${dataType.SortOrder}_${dataType.Name}`}
              color={dataType.DisplayOnRank ? 'success' : 'danger'}
              disabled={isUpdatingDataType}
              onClick={(e) => {
                updateValue('DisplayOnRank', !dataType.DisplayOnRank);
              }}
            >
              <FontAwesomeIcon icon={dataType.DisplayOnRank ? faEye : faEyeSlash} />
            </Button>
          </div>
          <div style={{ paddingLeft: 5 }}>
            <Button
              color={dataType.DisplayToEvaluator ? 'success' : 'danger'}
              disabled={isUpdatingDataType}
              onClick={(e) => {
                updateValue('DisplayToEvaluator', !dataType.DisplayToEvaluator);
              }}
            >
              <FontAwesomeIcon
                id={`DisplayToEvaluator_${dataType.SortOrder}`}
                icon={dataType.DisplayToEvaluator ? faEye : faEyeSlash}
              />
            </Button>
          </div>
        </div>
      );
    }
  };
  if (isFieldConfigurationConflict(dataType)) {
    return (
      <div role="alert" style={{ ...style.manageFieldsRow, padding: 12, flexDirection: 'column' }}>
        <strong>{Alias || dataType.Name}</strong>
        <span>Configuration conflict. This field is unavailable in Candidate Details and cannot be edited until support repairs the duplicate definitions.</span>
        {departmentId !== undefined && departmentId !== null ? <span>Contact support with department ID {departmentId} and field name {dataType.Name}.</span> : null}
      </div>
    );
  }
  if (!dataType.Enabled) {
    return (
      <div style={style.manageFieldsRow} key={dataType.Name}>
        <div style={{ ...style.manageFieldsName, width: '100%' }}>
          <div style={style.manageFieldsLabel}>{Alias}</div>

          <div style={{ textAlign: 'center', fontSize: 12, fontWeight: 'bold', wordWrap: 'break-word' }}>
            {dataType.Name.indexOf('CustomField') == 0 ? dataType.Name : null}
          </div>
        </div>

        <div>
          <Button
            disabled={isUpdatingDataType}
            color="primary"
            onClick={() => {
              if (updateDataType) {
                updateValue('Enabled', true);
              }
            }}
          >
            <FontAwesomeIcon icon={faArrowAltCircleRight} />
          </Button>
        </div>
      </div>
    );
  } else {
    return renderActiveItemFields();
  }
};

export default DataTypeItem;
